import React, { useEffect, useState } from 'react';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScienceIcon from '@mui/icons-material/Science';
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { createProject, Project } from '../api/projects';
import {
  createApiKey,
  createServiceAccount,
  listApiKeys,
  listServiceAccounts,
  ServiceAccount,
} from '../api/serviceAccounts';
import PageHeader from '../components/PageHeader';
import ActionButton from '../components/common/ActionButton';
import ResourceLink from '../components/common/ResourceLink';
import { useProject } from '../context/ProjectContext';
import { normalizeUserFacingError } from '../utils/normalizeUserFacingError';
import { trackFrontendEvent } from '../api/events';

const benchmarkRepositoryUrl = 'https://github.com/Quantik-Mind/quantikmind-benchmark-online-boutique';
const benchmarkRepositoryDirectory = 'quantikmind-benchmark-online-boutique';
const upstreamOnlineBoutiqueRepositoryUrl = 'https://github.com/GoogleCloudPlatform/microservices-demo.git';
const upstreamOnlineBoutiqueDirectory = 'microservices-demo';
const upstreamOnlineBoutiqueCommit = 'b7ecc96372238b0dda6cefaf95cc2c3e9118ea73';
const canonicalLibraryPath = 'qmind-test-library/online-boutique-playwright-51.json';
const benchmarkPlaywrightTestCount = 52;

const artifactCommands = [
  `git clone ${benchmarkRepositoryUrl}`,
  `cd ${benchmarkRepositoryDirectory}`,
  '.\\benchmark-pipeline\\generate-final-comparison.ps1 -UseExistingQMindSelections',
];

const qmindCommands = [
  `git clone ${benchmarkRepositoryUrl}`,
  `cd ${benchmarkRepositoryDirectory}`,
  '',
  '# Clone and deploy the pinned upstream Online Boutique application.',
  `git clone ${upstreamOnlineBoutiqueRepositoryUrl} ${upstreamOnlineBoutiqueDirectory}`,
  `git -C ${upstreamOnlineBoutiqueDirectory} checkout ${upstreamOnlineBoutiqueCommit}`,
  `Push-Location ${upstreamOnlineBoutiqueDirectory}`,
  'skaffold run',
  'Pop-Location',
  '# Run this port-forward in a separate terminal while the live benchmark runs:',
  'kubectl port-forward service/frontend 8080:80',
  '',
  '$env:QMIND_API_URL="https://api.quantikmind.com/api/v1"',
  `$env:QMIND_PROJECT_ID="${'<QMIND_PROJECT_ID>'}"`,
  '$env:QMIND_API_KEY="<QMIND_API_KEY>"',
  '',
  'qmind init `',
  '  --api-key "$env:QMIND_API_KEY" `',
  '  --api-url "$env:QMIND_API_URL" `',
  '  --project-id "$env:QMIND_PROJECT_ID" `',
  '  --framework playwright `',
  '  --test-dir qmind-test-harness/playwright/tests `',
  '  --results-dir qmind-test-harness/playwright/test-results `',
  '  --non-interactive',
  '',
  `qmind sync library --manifest ${canonicalLibraryPath}`,
  'qmind record build',
  '# qmind record tests imports JUnit XML when live Playwright results are available.',
  'qmind record tests --results-dir qmind-test-harness/playwright/test-results',
  '# The benchmark synthetic history import is benchmark-repository-specific.',
  '.\\benchmark-pipeline\\import-history.ps1',
  'qmind observability configure prometheus --file qmind-config/observability-online-boutique.example.yaml',
  'qmind status',
  '# Replace <CHANGED_FILES_FILE> with the changed-files file for the benchmark case you want to run.',
  '# Example: qmind-benchmark-cases/case-01/changed-files.txt',
  'qmind subset --framework playwright --json --changed-files-file "<CHANGED_FILES_FILE>" > qmind-selection.json',
  '',
  '# Final benchmark comparison generation is benchmark-repository-specific.',
  '# The qmind CLI requests the live selection; the comparison artifact is generated by this script.',
  '.\\benchmark-pipeline\\generate-final-comparison.ps1',
];

const windowsHelperCommands = [
  '# Optional alternative to the qmind CLI path above.',
  '# Run this option instead of Option A, not after it.',
  `git clone ${benchmarkRepositoryUrl}`,
  `cd ${benchmarkRepositoryDirectory}`,
  `git clone ${upstreamOnlineBoutiqueRepositoryUrl} ${upstreamOnlineBoutiqueDirectory}`,
  `git -C ${upstreamOnlineBoutiqueDirectory} checkout ${upstreamOnlineBoutiqueCommit}`,
  `Push-Location ${upstreamOnlineBoutiqueDirectory}`,
  'skaffold run',
  'Pop-Location',
  '# Run this port-forward in a separate terminal while the live benchmark runs:',
  'kubectl port-forward service/frontend 8080:80',
  'Copy-Item .env.example .env',
  '.\\benchmark-pipeline\\setup-qmind.ps1',
  '.\\benchmark-pipeline\\sync-library.ps1',
  '.\\benchmark-pipeline\\import-history.ps1',
  '.\\benchmark-pipeline\\configure-observability.ps1',
  '.\\benchmark-pipeline\\generate-final-comparison.ps1',
];

const webSetupSteps = [
  'Create Project',
  'Create Service Account',
  'Generate API Key',
];

export default function BenchmarkOnboardingPage() {
  const navigate = useNavigate();
  const { currentProject, setCurrentProject, refreshProjects } = useProject();
  const [projectName, setProjectName] = useState('');
  const [benchmarkProject, setBenchmarkProject] = useState<Project | null>(null);
  const [projectLoading, setProjectLoading] = useState(false);
  const [projectError, setProjectError] = useState<string | null>(null);
  const [repoCopied, setRepoCopied] = useState(false);
  const [credentialCheckLoading, setCredentialCheckLoading] = useState(false);
  const [hasServiceAccount, setHasServiceAccount] = useState(false);
  const [hasApiKey, setHasApiKey] = useState(false);
  const [serviceAccounts, setServiceAccounts] = useState<ServiceAccount[]>([]);
  const [selectedServiceAccount, setSelectedServiceAccount] = useState<ServiceAccount | null>(null);
  const [serviceAccountName, setServiceAccountName] = useState('online-boutique-benchmark-ci');
  const [serviceAccountDescription, setServiceAccountDescription] = useState('Service account for Online Boutique benchmark reproduction.');
  const [serviceAccountLoading, setServiceAccountLoading] = useState(false);
  const [serviceAccountError, setServiceAccountError] = useState<string | null>(null);
  const [apiKeyName, setApiKeyName] = useState('online-boutique-benchmark-key');
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [apiKeyError, setApiKeyError] = useState<string | null>(null);
  const [oneTimeApiKey, setOneTimeApiKey] = useState<string | null>(null);
  const [commandsCopied, setCommandsCopied] = useState(false);

  const onboardingProject = benchmarkProject || currentProject;

  const handleCreateBenchmarkProject = async () => {
    const trimmedName = projectName.trim();
    if (!trimmedName) {
      setProjectError('Project name is required.');
      return;
    }

    setProjectLoading(true);
    setProjectError(null);

    try {
      const createdProject = await createProject(trimmedName);
      setBenchmarkProject(createdProject);
      setCurrentProject(createdProject);
      await refreshProjects();
    } catch (error) {
      setProjectError(
        normalizeUserFacingError(error, 'Failed to create benchmark project. Please try again.')
      );
    } finally {
      setProjectLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const loadCredentialState = async () => {
      if (!onboardingProject?.id) {
        setHasServiceAccount(false);
        setHasApiKey(false);
        return;
      }

      setCredentialCheckLoading(true);
      try {
        const accounts = await listServiceAccounts(onboardingProject.id);
        if (cancelled) {
          return;
        }
        const activeAccounts = accounts.filter((account) => account.is_active);
        setServiceAccounts(activeAccounts);
        setSelectedServiceAccount(activeAccounts[0] || null);
        setHasServiceAccount(activeAccounts.length > 0);

        const keyLists = await Promise.all(
          activeAccounts.map((account) =>
            listApiKeys(account.id).catch(() => [])
          )
        );
        if (cancelled) {
          return;
        }
        setHasApiKey(
          keyLists.some((keys) =>
            keys.some((key) => key.is_active && !key.revoked_at)
          )
        );
      } catch {
        if (!cancelled) {
          setHasServiceAccount(false);
          setHasApiKey(false);
          setServiceAccounts([]);
          setSelectedServiceAccount(null);
        }
      } finally {
        if (!cancelled) {
          setCredentialCheckLoading(false);
        }
      }
    };

    void loadCredentialState();

    return () => {
      cancelled = true;
    };
  }, [onboardingProject?.id]);

  const projectReady = !!onboardingProject;
  const serviceAccountStepCompleted = hasServiceAccount || hasApiKey;
  const apiKeyStepCompleted = hasApiKey;
  const serviceAccountStepIsCurrent = projectReady && !serviceAccountStepCompleted;
  const apiKeyStepIsCurrent = serviceAccountStepCompleted && !apiKeyStepCompleted;

  const getStepStatus = (index: number) => {
    if (index === 0) {
      return projectReady ? 'completed' : 'current';
    }
    if (index === 1) {
      if (serviceAccountStepCompleted) {
        return 'completed';
      }
      if (serviceAccountStepIsCurrent) {
        return 'current';
      }
    }
    if (index === 2) {
      if (apiKeyStepCompleted) {
        return 'completed';
      }
      if (apiKeyStepIsCurrent) {
        return 'current';
      }
    }
    return 'pending';
  };

  const commandPack = [
    '# Quantik Mind Online Boutique Benchmark Commands',
    '',
    `Project: ${onboardingProject?.name || '<QMIND_PROJECT_NAME>'}`,
    `Project ID: ${onboardingProject?.id || '<QMIND_PROJECT_ID>'}`,
    `Service account: ${selectedServiceAccount?.name || serviceAccountName || '<SERVICE_ACCOUNT_NAME>'}`,
    `Canonical test library: ${canonicalLibraryPath} (${benchmarkPlaywrightTestCount} Playwright tests)`,
    `Pinned upstream Online Boutique commit: ${upstreamOnlineBoutiqueCommit}`,
    '',
    'Store the API key securely. The raw key is shown only once in Quantik Mind and is not included in this command pack.',
    '',
    '## Reproduce published benchmark',
    'No Quantik Mind project, API key, Prometheus, or live test selections are required.',
    '',
    ...artifactCommands,
    '',
    '## Run benchmark live',
    'Choose one live-run path. Do not run both Option A and Option B.',
    '',
    '### Option A — qmind CLI',
    'Use this path for product setup, library/history sync, Prometheus configuration, and requesting a live selection from Quantik Mind.',
    'Paste your one-time API key into QMIND_API_KEY before running qmind commands.',
    'Final comparison generation is currently available through the benchmark-pipeline PowerShell script, not a qmind CLI command.',
    '',
    ...qmindCommands.map((command) =>
      command === '$env:QMIND_PROJECT_ID="<QMIND_PROJECT_ID>"'
        ? `$env:QMIND_PROJECT_ID="${onboardingProject?.id || '<QMIND_PROJECT_ID>'}"`
        : command
    ),
    '',
    '### Option B — Windows helper scripts',
    'Optional alternative. These scripts are benchmark-repository helpers for Windows PowerShell. Run this option instead of Option A.',
    '',
    ...windowsHelperCommands,
  ].join('\n');

  const scrollToCommands = () => {
    document.getElementById('benchmark-commands')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleCreateServiceAccount = async () => {
    if (!onboardingProject?.id || !serviceAccountName.trim()) {
      return;
    }

    setServiceAccountLoading(true);
    setServiceAccountError(null);
    try {
      const created = await createServiceAccount({
        project_id: onboardingProject.id,
        name: serviceAccountName.trim(),
        description: serviceAccountDescription.trim() || undefined,
      });
      setServiceAccounts((current) => [created, ...current.filter((account) => account.id !== created.id)]);
      setSelectedServiceAccount(created);
      setHasServiceAccount(true);
    } catch (error) {
      setServiceAccountError(
        normalizeUserFacingError(error, 'Failed to create service account. Please try again.')
      );
    } finally {
      setServiceAccountLoading(false);
    }
  };

  const handleGenerateApiKey = async () => {
    if (!selectedServiceAccount?.id) {
      return;
    }

    setApiKeyLoading(true);
    setApiKeyError(null);
    try {
      const created = await createApiKey(selectedServiceAccount.id, {
        name: apiKeyName.trim() || undefined,
        scopes: ['library:read', 'library:write', 'observability:read', 'observability:write', 'build:read', 'build:write', 'selection:read', 'selection:run', 'results:read', 'results:write', 'history:read', 'history:write'],
      });
      setOneTimeApiKey(created.api_key);
      setHasApiKey(true);
    } catch (error) {
      setApiKeyError(
        normalizeUserFacingError(error, 'Failed to generate API key. Please try again.')
      );
    } finally {
      setApiKeyLoading(false);
    }
  };

  const copyOneTimeApiKey = async () => {
    if (oneTimeApiKey && navigator?.clipboard) {
      await navigator.clipboard.writeText(oneTimeApiKey);
    }
  };

  const closeOneTimeApiKeyPanel = () => {
    setOneTimeApiKey(null);
    scrollToCommands();
  };

  const copyCommandPack = async () => {
    if (navigator?.clipboard) {
      await navigator.clipboard.writeText(commandPack);
      setCommandsCopied(true);
      void trackFrontendEvent('ci_config_copied', { provider: 'benchmark_command_pack' });
      void trackFrontendEvent('cli_install_instructions_viewed', { onboarding_step: 'benchmark_commands' });
    }
  };

  const downloadCommandPack = () => {
    const blob = new Blob([commandPack], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'online-boutique-benchmark-commands.md';
    link.click();
    URL.revokeObjectURL(url);
    void trackFrontendEvent('ci_config_generated', { provider: 'benchmark_command_pack' });
  };

  const copyRepositoryUrl = async () => {
    if (navigator?.clipboard) {
      await navigator.clipboard.writeText(benchmarkRepositoryUrl);
      setRepoCopied(true);
    }
  };

  return (
    <Box className="page-transition" sx={{ width: '100%', maxWidth: 1040, mx: 'auto' }}>
      <PageHeader
        icon={<AssessmentIcon fontSize="inherit" />}
        title="Online Boutique Benchmark"
        subtitle="Evaluate Quantik Mind with Google's public Online Boutique microservices demo and benchmark assets."
      />

      <Paper
        elevation={3}
        sx={{
          borderRadius: 3,
          p: { xs: 2, md: 3 },
          background: 'linear-gradient(180deg, rgba(16, 20, 27, 0.98), rgba(10, 14, 20, 0.98))',
        }}
      >
        <Stack spacing={2.5}>
          <Card className="enterprise-card" sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 2.25, md: 3 } }}>
              <Stack spacing={2}>
                <Box sx={{ color: '#39FF14' }}>
                  <ScienceIcon fontSize="large" />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    Choose a benchmark mode
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Quantik Mind can be evaluated against Google's public Online
                    Boutique demo application. Use the artifact path to verify
                    published results, or run the benchmark live against your
                    own Quantik Mind workspace.
                  </Typography>
                </Box>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <Card
                    className="enterprise-card"
                    sx={{
                      flex: 1,
                      borderRadius: 2.5,
                      cursor: 'pointer',
                      transition: 'border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
                      '&:hover, &:focus-within': {
                        borderColor: 'rgba(57,255,20,0.42)',
                        boxShadow: '0 0 0 1px rgba(57,255,20,0.18), 0 16px 34px rgba(57,255,20,0.14)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <CardActionArea
                      onClick={scrollToCommands}
                      sx={{
                        height: '100%',
                        alignItems: 'stretch',
                        '&:focus-visible': {
                          outline: '2px solid #39ff14',
                          outlineOffset: -2,
                        },
                      }}
                    >
                      <CardContent sx={{ height: '100%' }}>
                        <Stack spacing={1.5}>
                          <Typography variant="subtitle1" fontWeight={700}>
                            Reproduce published benchmark
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Clone the benchmark repository and reproduce the
                            published comparison locally. No Quantik Mind project,
                            API key, service account, Prometheus, or live test
                            selections are required.
                          </Typography>
                        </Stack>
                      </CardContent>
                    </CardActionArea>
                  </Card>

                  <Card
                    className="enterprise-card"
                    sx={{
                      flex: 1,
                      borderRadius: 2.5,
                      cursor: 'pointer',
                      transition: 'border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
                      '&:hover, &:focus-within': {
                        borderColor: 'rgba(57,255,20,0.42)',
                        boxShadow: '0 0 0 1px rgba(57,255,20,0.18), 0 16px 34px rgba(57,255,20,0.14)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <CardActionArea
                      onClick={() => document.getElementById('benchmark-live-flow')?.scrollIntoView({ behavior: 'smooth' })}
                      sx={{
                        height: '100%',
                        alignItems: 'stretch',
                        '&:focus-visible': {
                          outline: '2px solid #39ff14',
                          outlineOffset: -2,
                        },
                      }}
                    >
                      <CardContent sx={{ height: '100%' }}>
                        <Stack spacing={1.5}>
                          <Typography variant="subtitle1" fontWeight={700}>
                            Run benchmark live
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Recreate the benchmark against your Quantik Mind
                            workspace using the qmind CLI, a benchmark project,
                            a service account API key, benchmark history,
                            Prometheus observability, and live selections.
                          </Typography>
                        </Stack>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Alert severity="info">
            Use the qmind CLI for the benchmark workflow. The CLI uses your
            service account API key to connect to Quantik Mind. Benchmark helper
            scripts are available as a secondary path when the repository
            documents a script-only step.
          </Alert>

          <Card className="enterprise-card" sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 2.25, md: 3 } }}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Benchmark Repository
                </Typography>
                <Typography
                  component="code"
                  sx={{
                    display: 'block',
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'rgba(0,0,0,0.22)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    overflowX: 'auto',
                  }}
                >
                  {benchmarkRepositoryUrl}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                  <ActionButton
                    onClick={() => void copyRepositoryUrl()}
                  >
                    {repoCopied ? 'URL copied' : 'Copy URL'}
                  </ActionButton>
                  <ResourceLink
                    href={benchmarkRepositoryUrl}
                    target="_blank"
                  >
                    Open repository
                  </ResourceLink>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Card id="benchmark-live-flow" className="enterprise-card" sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 2.25, md: 3 } }}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Live benchmark setup
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                    This path stays inside benchmark onboarding and does not
                    enter the standard Free onboarding flow for user-provided
                    data.
                  </Typography>
                </Box>

                <Stack spacing={1.25}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Step 1: Create benchmark project
                  </Typography>
                  <TextField
                    label="Project name"
                    value={projectName}
                    onChange={(event) => setProjectName(event.target.value)}
                    disabled={projectLoading || projectReady}
                    placeholder="Online Boutique Benchmark"
                    fullWidth
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems="flex-start">
                    <ActionButton
                      onClick={() => void handleCreateBenchmarkProject()}
                      disabled={projectLoading || projectReady || !projectName.trim()}
                      startIcon={projectLoading ? <CircularProgress size={16} /> : undefined}
                      actionVariant={projectReady ? 'active' : 'default'}
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      {projectReady ? 'Project ready' : 'Create benchmark project'}
                    </ActionButton>
                    {projectReady && (
                      <Chip
                        icon={<CheckCircleIcon />}
                        label={`Project ready: ${onboardingProject?.name}`}
                        sx={{
                          color: '#DFFFE6',
                          bgcolor: 'rgba(57, 255, 20, 0.12)',
                          border: '1px solid rgba(57, 255, 20, 0.2)',
                          '& .MuiChip-icon': { color: '#39FF14' },
                        }}
                      />
                    )}
                  </Stack>
                  {projectError && <Alert severity="error">{projectError}</Alert>}
                </Stack>

                <Stack spacing={1}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Benchmark Progress
                  </Typography>
                  {credentialCheckLoading && (
                    <Typography variant="caption" color="text.secondary">
                      Refreshing Service Accounts & API Keys status...
                    </Typography>
                  )}
                  <Stack spacing={1}>
                    {webSetupSteps.map((item, index) => {
                      const status = getStepStatus(index);
                      const statusLabel = status === 'completed'
                        ? 'Completed'
                        : status === 'current'
                          ? 'Current'
                          : 'Pending';
                      return (
                        <Stack
                          key={item}
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          alignItems={{ xs: 'flex-start', sm: 'center' }}
                          sx={{
                            p: 1,
                            borderRadius: 2,
                            bgcolor: status === 'current' ? 'rgba(57,255,20,0.08)' : 'rgba(255,255,255,0.02)',
                            border: status === 'current'
                              ? '1px solid rgba(57,255,20,0.22)'
                              : '1px solid rgba(255,255,255,0.06)',
                          }}
                        >
                          <Chip
                            size="small"
                            label={statusLabel}
                            color={status === 'completed' ? 'success' : 'default'}
                            variant={status === 'current' ? 'filled' : 'outlined'}
                            sx={{ minWidth: 92 }}
                          />
                          <Typography variant="body2" color={status === 'pending' ? 'text.secondary' : 'text.primary'}>
                            {index + 1}. {item}
                          </Typography>
                        </Stack>
                      );
                    })}
                  </Stack>
                </Stack>

                {projectReady && (
                  <Card className="enterprise-card" sx={{ borderRadius: 2.5 }}>
                    <CardContent>
                      <Stack spacing={1.5}>
                        <Typography variant="subtitle2" fontWeight={700}>
                          Step 2: Create Service Account
                        </Typography>
                        <TextField
                          label="Service account name"
                          value={serviceAccountName}
                          onChange={(event) => setServiceAccountName(event.target.value)}
                          disabled={serviceAccountLoading || serviceAccountStepCompleted}
                          fullWidth
                        />
                        <TextField
                          label="Description"
                          value={serviceAccountDescription}
                          onChange={(event) => setServiceAccountDescription(event.target.value)}
                          disabled={serviceAccountLoading || serviceAccountStepCompleted}
                          fullWidth
                        />
                        <ActionButton
                          onClick={() => void handleCreateServiceAccount()}
                          disabled={serviceAccountLoading || serviceAccountStepCompleted || !serviceAccountName.trim()}
                          startIcon={serviceAccountLoading ? <CircularProgress size={16} /> : undefined}
                          actionVariant={serviceAccountStepCompleted ? 'active' : 'default'}
                          sx={{ alignSelf: 'flex-start' }}
                        >
                          {serviceAccountStepCompleted ? 'Service account ready' : 'Create service account'}
                        </ActionButton>
                        {serviceAccountStepCompleted && selectedServiceAccount && (
                          <Chip
                            icon={<CheckCircleIcon />}
                            label={`Service account ready: ${selectedServiceAccount.name}`}
                            sx={{
                              alignSelf: 'flex-start',
                              color: '#DFFFE6',
                              bgcolor: 'rgba(57, 255, 20, 0.12)',
                              border: '1px solid rgba(57, 255, 20, 0.2)',
                              '& .MuiChip-icon': { color: '#39FF14' },
                            }}
                          />
                        )}
                        {serviceAccountError && <Alert severity="error">{serviceAccountError}</Alert>}
                      </Stack>
                    </CardContent>
                  </Card>
                )}

                {serviceAccountStepCompleted && selectedServiceAccount && (
                  <Card className="enterprise-card" sx={{ borderRadius: 2.5 }}>
                    <CardContent>
                      <Stack spacing={1.5}>
                        <Typography variant="subtitle2" fontWeight={700}>
                          Step 3: Generate API Key
                        </Typography>
                        <TextField
                          label="API key name"
                          value={apiKeyName}
                          onChange={(event) => setApiKeyName(event.target.value)}
                          disabled={apiKeyLoading || apiKeyStepCompleted}
                          fullWidth
                        />
                        <ActionButton
                          onClick={() => void handleGenerateApiKey()}
                          disabled={apiKeyLoading || apiKeyStepCompleted}
                          startIcon={apiKeyLoading ? <CircularProgress size={16} /> : undefined}
                          actionVariant={apiKeyStepCompleted ? 'active' : 'default'}
                          sx={{ alignSelf: 'flex-start' }}
                        >
                          {apiKeyStepCompleted ? 'API key generated' : 'Generate API key'}
                        </ActionButton>
                        {apiKeyStepCompleted && (
                          <Chip
                            icon={<CheckCircleIcon />}
                            label="API key generated"
                            sx={{
                              alignSelf: 'flex-start',
                              color: '#DFFFE6',
                              bgcolor: 'rgba(57, 255, 20, 0.12)',
                              border: '1px solid rgba(57, 255, 20, 0.2)',
                              '& .MuiChip-icon': { color: '#39FF14' },
                            }}
                          />
                        )}
                        {apiKeyError && <Alert severity="error">{apiKeyError}</Alert>}
                        {oneTimeApiKey && (
                          <Alert severity="warning">
                            <Stack spacing={1.25}>
                              <Typography fontWeight={700}>
                                Copy this API key now. It will not be shown again.
                              </Typography>
                              <Box
                                component="code"
                                sx={{
                                  display: 'block',
                                  p: 1.25,
                                  borderRadius: 1.5,
                                  bgcolor: 'rgba(0,0,0,0.26)',
                                  overflowX: 'auto',
                                }}
                              >
                                {oneTimeApiKey}
                              </Box>
                              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                <ActionButton onClick={() => void copyOneTimeApiKey()}>
                                  Copy key
                                </ActionButton>
                                <ActionButton onClick={closeOneTimeApiKeyPanel}>
                                  Continue to local commands
                                </ActionButton>
                              </Stack>
                            </Stack>
                          </Alert>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                )}

                {apiKeyStepCompleted && (
                  <Alert severity="success">
                    <Stack spacing={0.75}>
                      <Typography fontWeight={700}>Web setup completed:</Typography>
                      <Typography>✓ Project created</Typography>
                      <Typography>✓ Service account created</Typography>
                      <Typography>✓ API key generated</Typography>
                      <Typography sx={{ mt: 0.5 }}>
                        Next: run the benchmark locally.
                      </Typography>
                    </Stack>
                  </Alert>
                )}
              </Stack>
            </CardContent>
          </Card>

          <Card id="benchmark-commands" className="enterprise-card" sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 2.25, md: 3 } }}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Benchmark commands
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                    These commands are specific to the Quantik Mind Online
                    Boutique Benchmark repository. They are benchmark commands,
                    not general product CLI commands.
                  </Typography>
                </Box>
                {apiKeyStepCompleted && (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                    <ActionButton onClick={() => void copyCommandPack()}>
                      {commandsCopied ? 'Commands copied' : 'Copy all commands'}
                    </ActionButton>
                    <ActionButton onClick={downloadCommandPack}>
                      Download commands
                    </ActionButton>
                  </Stack>
                )}
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Reproduce published benchmark
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                    Start from a clean machine by cloning the benchmark
                    repository, then recompute the committed final comparison
                    from existing artifacts. This does not require a Quantik
                    Mind project, API key, Prometheus, or live test selection.
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      mt: 1,
                      p: 1.5,
                      overflowX: 'auto',
                      borderRadius: 2,
                      bgcolor: 'rgba(0,0,0,0.22)',
                      border: '1px solid rgba(255,255,255,0.08)',
                    }}
                  >
                    <code>{artifactCommands.join('\n')}</code>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Run benchmark live
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                    Choose one of the two alternatives below. Option A uses the
                    supported qmind CLI commands for setup, signal sync, and
                    live selection. Option B uses benchmark-repository Windows
                    helper scripts as an optional alternative. Do not run both.
                    The canonical library is {canonicalLibraryPath}, a historical
                    filename that contains {benchmarkPlaywrightTestCount} Playwright
                    tests. Live runs deploy the upstream Online Boutique checkout
                    pinned to {upstreamOnlineBoutiqueCommit}.
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Option A — qmind CLI
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                    The qmind CLI handles project configuration, library sync,
                    build and test-result import, Prometheus configuration,
                    readiness checks, and the live selection request. Final
                    benchmark comparison generation and synthetic benchmark
                    history import are currently provided by benchmark-pipeline
                    PowerShell scripts, not by standalone qmind CLI commands.
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      mt: 1,
                      p: 1.5,
                      overflowX: 'auto',
                      borderRadius: 2,
                      bgcolor: 'rgba(0,0,0,0.22)',
                      border: '1px solid rgba(255,255,255,0.08)',
                    }}
                  >
                    <code>{qmindCommands.join('\n')}</code>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Option B — Windows helper scripts
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                    Optional alternative for Windows PowerShell. Use this path
                    instead of Option A when you want the benchmark repository
                    helper scripts to drive setup and comparison generation.
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      mt: 1,
                      p: 1.5,
                      overflowX: 'auto',
                      borderRadius: 2,
                      bgcolor: 'rgba(0,0,0,0.22)',
                      border: '1px solid rgba(255,255,255,0.08)',
                    }}
                  >
                    <code>{windowsHelperCommands.join('\n')}</code>
                  </Box>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <ActionButton
              onClick={() => navigate('/onboarding')}
              sx={{ alignSelf: 'flex-start' }}
            >
              Back to onboarding choices
            </ActionButton>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
